"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Header } from "@/components/header";
import { useState, useEffect, useRef } from "react";
import { HeroSection } from "@/components/hero-section";
import { useInView } from "@/hooks/useInView";
import { researchPapersApi, categoryApi, teamsApi, employeeApi, contentInfoApi } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

import dynamic from "next/dynamic";

// Lazy load Footer
const Footer = dynamic(() => import("@/components/footer").then(mod => ({ default: mod.Footer })), {
  loading: () => <div className="h-75 bg-gray-200 animate-pulse" />,
});

// Lazy load ChatWidget
const ChatWidget = dynamic(() => import("@/components/chat-widget"), {
  loading: () => null,
  ssr: false,
});

interface Category {
  id: string;
  name_category: string;
}

interface Employee {
  id: string;
  name: string;
}

interface TeamEmployee {
  id: string;
  name: string;
}

interface TeamMembership {
  id: string;
  id_team: string;
  id_employee: string;
  employee?: TeamEmployee;
}

interface TeamWithMembers {
  id?: string;
  id_team?: string;
  name_team: string;
  team_memberships?: TeamMembership[];
}

interface ResearchData {
  id: string;
  title: string;
  abstract: string;
  keywords: string;
  additional_info: string;
  slug: string;
  published_at: string;
  id_category: string;
  id_team: string;
  is_published: boolean;
  is_active: boolean;
  research_url?: string;
}

interface ResearchPaper {
  id: string;
  category: string;
  date: string;
  dateRaw: string;
  author: string;
  title: string;
  abstract: string;
  tags: string[];
  publication: string;
  link: string;
  research_url?: string;
}

interface ContentInfo {
  id_content: string;
  content_title: string;
  content_description?: string | null;
  type?: string | null;
}

export default function Research() {
  const { language } = useLanguage();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [expandedPapers, setExpandedPapers] = useState<Set<string>>(new Set());
  const [contentRef, contentInView] = useInView<HTMLDivElement>({
    threshold: 0.1,
  });
  const [papersRef, papersInView] = useInView<HTMLDivElement>({
    threshold: 0.1,
  });
  const [researchPapers, setResearchPapers] = useState<ResearchPaper[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [heroContent, setHeroContent] = useState<ContentInfo | null>(null);

  const datePickerRef = useRef<HTMLDivElement>(null);
  const categoryRef = useRef<HTMLDivElement>(null);

  const ITEMS_PER_PAGE = 2;

  const labels = {
    en: {
      readPaper: "Read Paper",
      visitPage: "Visit Page",
      showMore: "show more",
      showLess: "show less",
      allDates: "All Dates",
      dateRange: "Date Range",
      startDate: "Start Date",
      endDate: "End Date",
      category: "Category",
      allCategories: "All Categories",
      findResearch: "Find research",
      research: "Research",
      clear: "Clear",
    },
    id: {
      readPaper: "Baca Makalah",
      visitPage: "Kunjungi Halaman",
      showMore: "tampilkan lebih banyak",
      showLess: "tampilkan lebih sedikit",
      allDates: "Semua Tanggal",
      dateRange: "Rentang Tanggal",
      startDate: "Tanggal Mulai",
      endDate: "Tanggal Selesai",
      category: "Kategori",
      allCategories: "Semua Kategori",
      findResearch: "Cari riset",
      research: "Riset",
      clear: "Bersihkan",
    },
  };

  const t = labels[language];

  useEffect(() => {
    let isMounted = true;

    const fetchHeroContent = async () => {
      try {
        const response = await contentInfoApi.getAll({ limit: 1, type: "research", lang: language });
        const data = (response.data?.data || response.data) as ContentInfo[];

        if (!isMounted) return;
        setHeroContent(data?.[0] || null);
      } catch (err) {
        if (isMounted) setHeroContent(null);
        console.log("Error fetching research hero content:", err);
      }
    };

    fetchHeroContent();
    return () => {
      isMounted = false;
    };
  }, [language]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setIsDatePickerOpen(false);
      }
      if (
        categoryRef.current &&
        !categoryRef.current.contains(event.target as Node)
      ) {
        setIsCategoryOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter research papers based on search, category, and date
  const filteredPapers = researchPapers.filter((paper) => {
    const matchesSearch =
      searchQuery === "" ||
      paper.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      paper.abstract.toLowerCase().includes(searchQuery.toLowerCase()) ||
      paper.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
      paper.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      paper.date.toLowerCase().includes(searchQuery.toLowerCase()) ||
      paper.publication.toLowerCase().includes(searchQuery.toLowerCase()) ||
      paper.tags.some((tag) =>
        tag.toLowerCase().includes(searchQuery.toLowerCase())
      );

    const matchesCategory =
      selectedCategory === "all" ||
      paper.category.toLowerCase() === selectedCategory.toLowerCase();

    // Date filtering logic
    let matchesDate = true;
    if (startDate || endDate) {
      const paperDate = new Date(paper.date);
      if (startDate && paperDate < new Date(startDate)) matchesDate = false;
      if (endDate && paperDate > new Date(endDate)) matchesDate = false;
    }

    return matchesSearch && matchesCategory && matchesDate;
  });

  // Get unique categories
  const categories = Array.from(new Set(researchPapers.map((paper) => paper.category)));

  const toggleExpand = (paperId: string) => {
    setExpandedPapers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(paperId)) {
        newSet.delete(paperId);
      } else {
        newSet.add(paperId);
      }
      return newSet;
    });
  };

  // Fetch research papers from API
  useEffect(() => {
    const fetchResearchPapers = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all required data in parallel
        const [researchRes, categoriesRes, teamsRes, employeesRes] =
          await Promise.all([
            researchPapersApi.getAll({ limit: 100, is_published: true, lang: language }),
            categoryApi.getAll({ limit: 100, lang: language }),
            teamsApi.getAll({ limit: 100 }),
            employeeApi.getAll({ limit: 100 }),
          ]);

        const research = (researchRes.data.data || researchRes.data) as ResearchData[];
        const categories = (categoriesRes.data.data || categoriesRes.data) as Category[];
        const teams = (teamsRes.data.data || teamsRes.data) as TeamWithMembers[];
        const employees = (employeesRes.data.data || employeesRes.data) as Employee[];

        // Create lookup maps
        const categoryMap = new Map(
          categories.map((cat: Category) => [cat.id, cat.name_category])
        );
        const employeeMap = new Map(
          employees.map((emp: Employee) => [emp.id, emp.name])
        );

        // Create team to employees mapping from Team API response (includes team_memberships)
        const teamToEmployeesMap = new Map<string, string[]>();
        teams.forEach((team: TeamWithMembers) => {
          const teamId = team.id_team || team.id || '';
          const teamMembers = team.team_memberships || [];
          const employeeNames: string[] = [];

          teamMembers.forEach((membership: TeamMembership) => {
            const employee = membership.employee;
            if (employee && employee.name) {
              employeeNames.push(employee.name);
            } else if (membership.id_employee) {
              const employeeName = employeeMap.get(membership.id_employee);
              if (employeeName) {
                employeeNames.push(employeeName);
              }
            }
          });

          if (employeeNames.length > 0) {
            teamToEmployeesMap.set(teamId, employeeNames);
          }
        });

        // Format date helper
        const formatDate = (dateString: string | null) => {
          const locale = language === "id" ? "id-ID" : "en-US";
          if (!dateString)
            return new Date().toLocaleDateString(locale, {
              year: "numeric",
              month: "long",
              day: "numeric",
            });
          const date = new Date(dateString);
          return date.toLocaleDateString(locale, {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
        };

        // Transform research papers (only active papers)
        const transformedPapers = research
          .filter((paper: ResearchData) => paper.is_active === true)
          .map((paper: ResearchData) => {
            // Get employee names from the team
            const teamEmployeeNames = teamToEmployeesMap.get(paper.id_team) || [];
            const authorNames =
              teamEmployeeNames.length > 0
                ? teamEmployeeNames.join(", ")
                : "Research Team";

            const researchLabel = t.research;
            return {
              id: paper.id ?? paper.slug ?? `${(paper.title || '').replace(/\s+/g, "-").toLowerCase()}-${paper.published_at || ''}`,
              category: categoryMap.get(paper.id_category) || researchLabel,
              date: formatDate(paper.published_at),
              dateRaw: paper.published_at || new Date().toISOString(),
              author: authorNames,
              title: paper.title || "",
              abstract: paper.abstract || "",
              tags: paper.keywords
                ? paper.keywords.split(",").map((tag: string) => tag.trim())
                : [],
              publication: paper.additional_info || "",
              link: paper.slug,
              research_url: paper.research_url,
            };
          })
          .sort((a, b) => {
            // Sort by date descending (latest first)
            return new Date(b.dateRaw).getTime() - new Date(a.dateRaw).getTime();
          });

        console.log("Transformed papers:", transformedPapers);
        setResearchPapers(transformedPapers);
      } catch (err) {
        console.log("Error fetching research papers:", err);
        setError("Failed to load research papers");
        setResearchPapers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchResearchPapers();
  }, [language, t.research]);

  const getTruncatedAbstract = (abstract: string, maxLength: number = 200) => {
    if (abstract.length <= maxLength) return abstract;
    const truncated = abstract.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(" ");
    return truncated.substring(0, lastSpace) + "...";
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredPapers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentPapers = filteredPapers.slice(startIndex, endIndex);
  const pagesToShow: (number | string)[] = [];

  // Pagination logic
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) {
      pagesToShow.push(i);
    }
  } else {
    if (currentPage <= 3) {
      pagesToShow.push(1, 2, 3, 4, "...", totalPages);
    } else if (currentPage >= totalPages - 2) {
      pagesToShow.push(
        1,
        "...",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    } else {
      pagesToShow.push(
        1,
        "...",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "...",
        totalPages
      );
    }
  }

  return (
    <div className="page-layout bg-white">
      <div className="page-content">
        {/* Navigation Bar */}
        <Header activePage="research" />

        {/* Hero Section */}
        <HeroSection
          title={heroContent?.content_title || "Our Research"}
          subtitle={heroContent?.content_description || "Explore our latest publications and academic contributers"}
        />

        {/* Research Content Section */}
        <section className="py-12 px-8 lg:px-16 bg-white">
          <div className="max-w-7xl mx-auto">
            {/* Search and Filter Bar */}
            <div
              ref={contentRef}
              className={`relative z-30 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 reveal ${contentInView ? "is-visible" : ""}`}
            >
              {/* Search Input */}
              <div className="relative w-full md:w-auto">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <Input
                  type="text"
                  placeholder={t.findResearch}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full md:w-64"
                />
              </div>

              {/* Filter Buttons */}
              <div className="flex gap-3">
                <div className="relative" ref={datePickerRef}>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsDatePickerOpen(!isDatePickerOpen);
                      setIsCategoryOpen(false);
                    }}
                    className="flex items-center gap-2 border-gray-300 hover:bg-gray-50"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    {startDate || endDate ? t.dateRange : t.allDates}
                  </Button>
                  {isDatePickerOpen && (
                    <div className="absolute left-0 mt-2 w-72 bg-white border border-gray-300 rounded-md shadow-lg z-999 p-4">
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.startDate}
                          </label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.endDate}
                          </label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div className="flex gap-2 pt-2">
                          <Button
                            onClick={() => {
                              setStartDate("");
                              setEndDate("");
                              setIsDatePickerOpen(false);
                            }}
                            variant="outline"
                            className="flex-1 text-sm"
                          >
                            {t.clear}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="relative" ref={categoryRef}>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsCategoryOpen(!isCategoryOpen);
                      setIsDatePickerOpen(false);
                    }}
                    className="flex items-center gap-2 border-gray-300 hover:bg-gray-50"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                      />
                    </svg>
                    {selectedCategory === "all" ? t.category : selectedCategory}
                  </Button>
                  {isCategoryOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-999">
                      <button
                        onClick={() => {
                          setSelectedCategory("all");
                          setIsCategoryOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-black"
                      >
                        {t.allCategories}
                      </button>
                      {categories.map((category) => (
                        <button
                          key={category}
                          onClick={() => {
                            setSelectedCategory(category);
                            setIsCategoryOpen(false);
                          }}
                          className="block w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-black"
                        >
                          {category}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Research Papers List */}
            <div
              ref={papersRef}
              className={`relative z-0 space-y-6 reveal ${papersInView ? "is-visible reveal-delay-200" : ""
                }`}
            >
              {loading && (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">Loading research papers...</p>
                </div>
              )}

              {error && (
                <div className="text-center py-12">
                  <p className="text-red-500 text-lg">{error}</p>
                </div>
              )}

              {!loading && !error && currentPapers.length > 0 ? (
                currentPapers.map((paper, index) => (
                  <div
                    key={`${paper.id}-${index}`} // use index to ensure unique keys
                    className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow overflow-visible"
                  >
                    {/* Metadata */}
                    <div className="text-sm text-black mb-3">
                      {paper.category} • {paper.date} • {paper.author}
                    </div>

                    {/* Title */}
                    <h2 className="text-2xl md:text-3xl font-bold text-black mb-3">
                      {paper.title}
                    </h2>

                    {/* Abstract */}
                    <div className="text-black mb-4 leading-relaxed">
                      <p className="inline">
                        {expandedPapers.has(paper.id)
                          ? paper.abstract
                          : getTruncatedAbstract(paper.abstract)}

                        {paper.abstract.length > 150 && (
                          <button
                            onClick={() => toggleExpand(paper.id)}
                            className="ml-1 inline font-medium transition-colors"
                            style={{ color: "#0070F0" }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.color = "#002857")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.color = "#0070F0")
                            }
                          >
                            {expandedPapers.has(paper.id) ? t.showLess : t.showMore}
                          </button>
                        )}
                      </p>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {paper.tags.map((tag, idx) => (
                        <span
                          key={`${paper.id}-tag-${tag}-${idx}`} // ✅ fix: unique tag keys
                          className="px-3 py-1 text-xs border border-black rounded-full text-black bg-white hover:bg-gray-50 transition-colors"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Read Paper Button */}
                    {/* <div className="flex gap-3">
                    {paper.research_url && (
                      <a href={paper.research_url} target="_blank" rel="noopener noreferrer">
                        <Button
                          className="rounded-[10px] px-6 py-6 text-white font-semibold text-lg transition-opacity hover:opacity-90 flex items-center gap-2"
                          style={{
                            background:
                              "linear-gradient(135deg, #0C1A2E 0%, #285494 100%)",
                          }}
                        >
                          <span>Read Paper</span>
                          <Image src="/homepage/homepage-picture/arrow-right.svg"
                            alt="Arrow"
                            width={24}
                            height={24}
                            className="w-6 h-6"
                           loading="lazy" />
                        </Button>
                      </a>
                    )}
                    <a href={paper.link} target="_blank" rel="noopener noreferrer">
                      <Button
                        className="rounded-[10px] px-6 py-6 text-white font-semibold text-lg transition-opacity hover:opacity-90 flex items-center gap-2"
                        style={{
                          background:
                            "linear-gradient(135deg, #0C1A2E 0%, #285494 100%)",
                        }}
                      >
                        <span>Visit Page</span>
                        <Image src="/homepage/homepage-picture/arrow-right.svg"
                          alt="Arrow"
                          width={24}
                          height={24}
                          className="w-6 h-6"
                         loading="lazy" />
                      </Button>
                    </a>
                  </div> */}

                    <div className="flex w-full flex-row items-center justify-center md:justify-end gap-3 pt-4 flex-nowrap">
                      {paper.research_url && (
                        <a
                          href={paper.research_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button
                            className="shrink md:shrink-0 rounded-[10px] px-4 sm:px-6 py-2 min-w-[120px] md:min-w-[140px] text-white font-semibold text-lg shadow-md transition-all hover:opacity-90 hover:shadow-lg flex items-center justify-center gap-2"
                            style={{
                              background:
                                "linear-gradient(135deg, #0C1A2E 0%, #285494 100%)",
                            }}
                          >
                            {t.readPaper}
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="w-5 h-5"
                            >
                              <path d="M5 12h14" />
                              <path d="M13 6l6 6-6 6" />
                            </svg>
                          </Button>
                        </a>
                      )}
                      <a href={paper.link} target="_blank" rel="noopener noreferrer">
                        <Button
                          className="shrink md:shrink-0 rounded-[10px] px-4 sm:px-6 py-2 min-w-[120px] md:min-w-[140px] font-semibold text-lg border border-[#0C1A2E] text-[#0C1A2E] bg-white shadow-sm flex items-center justify-center gap-2"
                        >
                          {t.visitPage}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="w-5 h-5"
                          >
                            <path d="M5 12h14" />
                            <path d="M13 6l6 6-6 6" />
                          </svg>
                        </Button>

                      </a>
                    </div>

                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">
                    No research papers found matching your criteria.
                  </p>
                </div>
              )}
            </div>

            {/* Pagination */}
            <div className="flex justify-end items-center gap-2 mt-12">
              {/* Prev */}
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 rounded-md border border-gray-300 text-gray-700 hover:border-[#0C1A2E] hover:text-[#0C1A2E] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Pages */}
              {pagesToShow.map((page, idx) => (
                <button
                  key={`${page}-${idx}`}
                  onClick={() => typeof page === "number" && setCurrentPage(page)}
                  disabled={page === "..."}
                  className={`px-4 py-2 rounded-md border transition-colors ${page === currentPage
                    ? "bg-[#0C1A2E] text-white border-[#0C1A2E]"
                    : page === "..."
                      ? "border-gray-300 text-gray-400 cursor-default px-2"
                      : "border-gray-300 text-gray-700 hover:border-[#0C1A2E] hover:text-[#0C1A2E] hover:bg-gray-50"
                    }`}
                >
                  {page}
                </button>
              ))}

              {/* Next */}
              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 rounded-md border border-gray-300 text-gray-700 hover:border-[#0C1A2E] hover:text-[#0C1A2E] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <Footer />

      {/* Floating Chat Widget */}
      <ChatWidget />
    </div>
  );
}
